/**
 * AdSlot — universal ad container that works with Google AdSense.
 *
 * Behaviour:
 *   - Reads publisher client ID from `process.env.REACT_APP_ADSENSE_CLIENT`
 *     (format: `ca-pub-XXXXXXXXXXXXXXXX`).
 *   - Reads consent state from localStorage (set by ConsentBanner).
 *   - If either is missing, renders a subtle "AD" placeholder so the layout
 *     is preserved in development / on preview / before AdSense approval.
 *   - Once both are set, loads the AdSense script (once, globally) and
 *     initialises the slot via the standard `adsbygoogle` push pattern.
 *
 * Props:
 *   - slot: (required) AdSense slot ID from your AdSense dashboard
 *   - format: "auto" | "rectangle" | "horizontal" | "vertical" | "fluid"
 *   - layout: optional AdSense layout key ("in-article", "in-feed", …)
 *   - fullWidthResponsive: bool (default true)
 *   - style: passthrough style; height controls the slot's reserved space
 *   - label: shown above the slot to preserve editorial trust
 *   - className: passthrough
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";
const CONSENT_KEY = "ts_consent_v1";
let _adsenseScriptLoaded = false;

// True once a publisher ID is configured — pages use this to skip rendering
// ad wrappers entirely so unconfigured ads never reserve vertical space.
export const ADS_ENABLED = Boolean(ADSENSE_CLIENT);

function hasAdConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.ads === true;
  } catch {
    return false;
  }
}

function loadAdSenseOnce() {
  if (_adsenseScriptLoaded || !ADSENSE_CLIENT) return;
  if (typeof document === "undefined") return;
  const existing = document.querySelector('script[data-tts-adsense]');
  if (existing) {
    _adsenseScriptLoaded = true;
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(ADSENSE_CLIENT)}`;
  s.setAttribute("data-tts-adsense", "1");
  document.head.appendChild(s);
  _adsenseScriptLoaded = true;
}

export default function AdSlot({
  slot,
  format = "auto",
  layout,
  fullWidthResponsive = true,
  style = { display: "block", minHeight: 250 },
  label = "Advertisement",
  className = "",
  testId,
}) {
  const insRef = useRef(null);
  const { pathname } = useLocation();
  // Re-render on route change so slots re-fill for the new page.
  const [routeKey, setRouteKey] = useState(pathname);
  useEffect(() => { setRouteKey(pathname); }, [pathname]);

  const consented = hasAdConsent();
  const configured = Boolean(ADSENSE_CLIENT && slot);

  useEffect(() => {
    if (!configured || !consented) return;
    loadAdSenseOnce();
    // Push happens after the <ins> is in the DOM.
    const t = setTimeout(() => {
      try {
        // eslint-disable-next-line no-undef
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // Silent — AdSense sometimes throws on the same slot pushed twice.
      }
    }, 30);
    return () => clearTimeout(t);
  }, [configured, consented, routeKey, slot]);

  // Not configured or no consent → render NOTHING. An unfilled ad must never
  // create hundreds of pixels of dead space (was a big mobile UX complaint).
  // Once AdSense is configured + consented, the reserved minHeight keeps the
  // layout stable while the ad fills.
  if (!configured || !consented) return null;

  return (
    <div className={`ts-ad-slot ${className}`} data-testid={testId || `ad-slot-${slot}`}>
      <div
        className="ts-ad-slot__label"
        style={{
          fontSize: 9,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.4)",
          fontFamily: "'Barlow Condensed', sans-serif",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <ins
        ref={insRef}
        key={`${slot}-${routeKey}`}
        className="adsbygoogle"
        style={style}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-layout={layout || undefined}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      />
    </div>
  );
}
